import * as R from 'ramda';
import i18n from 'helpers/i18n';
import messages from './messages';
import RoutesHelper from 'helpers/routes';
import { useState } from 'react';
import { Button, Icon } from 'semantic-ui-react';
import MainContent from 'components/MainContent/MainContent';
import { useNavigate, Link } from 'react-router-dom';
import { CollapsibleConfigurationForm } from 'components/Reports/Observation/CollapsibleConfigurationForm';
import { OptionalDate } from 'components/Reports/Observation/DateRangePicker/DateRangePicker';
import { useAgeLevelOption, useCurrentAccountByArea } from '../Observation';
import PageLoader from 'components/PageLoader';
import { AlertCircle } from '@teachstonellc/component-library/dist/icons';

export type FormValues = {
  ageLevel: string | null;
  startDate: OptionalDate;
  endDate: OptionalDate;
  nodeIds: number[];
  classVersion?: number;
  classroomLevelDetails: boolean;
  includeArchivedClassrooms?: boolean;
  includeArchivedNodes?: boolean;
};

export type ReportConfig = {
  age_level: string | null;
  start_date: OptionalDate;
  end_date: OptionalDate;
  node_ids: number[];
  comparison_configuration?: ReportConfig;
  classroom_level_details: boolean;
  include_archived_classrooms?: boolean;
  include_archived_nodes?: boolean;
};

interface ReportConfigurationFormProps {
  title: string;
  subtitle?: string;
  initialValues: FormValues[];
  onSubmit: (configuration: ReportConfig) => void;
}

function ReportConfigurationForm({
  title,
  subtitle,
  initialValues,
  onSubmit
}: ReportConfigurationFormProps) {
  const navigate = useNavigate();
  const [totals, setTotals] = useState<number[]>([0]);
  const [configurations, setConfigurations] = useState<FormValues[]>(() =>
    initialValues.length > 1
      ? applyComparisonSharedFieldsFromPrimary(initialValues)
      : initialValues
  );

  const currentAccount = useCurrentAccountByArea('ObservationReports');
  const { ageLevelOptions, accountTemplates } = useAgeLevelOption(
    currentAccount?.guid
  );

  const classTemplateOptions = accountTemplates
    .filter(t => !t.env_scales)
    .map(t => ({
      key: t.id,
      text: t.name,
      value: t.class_version
    }));

  function allTotalsGreaterThanZero() {
    return R.all(t => t > 0, totals);
  }

  function allConfigurationsAreValid() {
    return R.all(
      config =>
        Boolean(
          config.startDate &&
          config.endDate &&
          config.ageLevel &&
          config.nodeIds.length > 0
        ),
      configurations
    );
  }

  const isSaveActive =
    allConfigurationsAreValid() && allTotalsGreaterThanZero();

  function mapFormConfigToAPIKeys(
    values: FormValues,
    sharedArchivedSource?: FormValues
  ): ReportConfig {
    const archivedSource = sharedArchivedSource ?? values;

    return {
      start_date: values.startDate,
      end_date: values.endDate,
      age_level: values.ageLevel,
      node_ids: values.nodeIds,
      classroom_level_details: values.classroomLevelDetails,
      include_archived_nodes: Boolean(archivedSource.includeArchivedNodes),
      include_archived_classrooms: Boolean(
        archivedSource.includeArchivedClassrooms
      )
    };
  }

  function handleSubmit() {
    const config1 = mapFormConfigToAPIKeys(configurations[0]);

    if (configurations.length === 2) {
      const config2 = mapFormConfigToAPIKeys(
        configurations[1],
        configurations[0]
      );
      onSubmit({ ...config1, comparison_configuration: config2 });
    } else {
      onSubmit(config1);
    }
  }

  function applyComparisonSharedFieldsFromPrimary(
    configs: FormValues[]
  ): FormValues[] {
    const primary = configs[0];

    return configs.map((config, idx) => {
      if (idx === 0) {
        return config;
      }

      return {
        ...config,
        ageLevel: primary.ageLevel,
        classVersion: primary.classVersion ?? config.classVersion,
        includeArchivedClassrooms:
          primary.includeArchivedClassrooms ??
          config.includeArchivedClassrooms ??
          true,
        includeArchivedNodes:
          primary.includeArchivedNodes ?? config.includeArchivedNodes ?? true
      };
    });
  }

  function handleConfigChange(idx: number, values: Partial<FormValues>) {
    setConfigurations(configurations => {
      const config = configurations[idx];
      const configs = R.update(idx, { ...config, ...values }, configurations);

      if (configs.length > 1 && idx === 0) {
        return applyComparisonSharedFieldsFromPrimary(configs);
      }

      return configs;
    });
  }

  function handleTotalChange(idx: number, total: number) {
    setTotals(R.update(idx, total, totals));
  }

  function handleAddComparison() {
    const comparisonConfig: FormValues = {
      startDate: null,
      endDate: null,
      ageLevel: configurations[0].ageLevel,
      classroomLevelDetails: false,
      nodeIds: [],
      includeArchivedClassrooms:
        configurations[0].includeArchivedClassrooms ?? true,
      includeArchivedNodes: configurations[0].includeArchivedNodes ?? true
    };

    setConfigurations(
      applyComparisonSharedFieldsFromPrimary(
        R.append(comparisonConfig, configurations)
      )
    );
    setTotals(R.append(0, totals));
  }

  function handleRemoveComparison(idx: number) {
    setConfigurations(R.remove(idx, 1, configurations));
    setTotals(R.remove(idx, 1, totals));
  }

  const isLoading =
    R.isEmpty(ageLevelOptions) || R.isEmpty(classTemplateOptions);

  return (
    <MainContent maxWidth={1280} className="!mt-10 text-black">
      <div className="mb-8">
        <Link
          className="text-black"
          to={RoutesHelper.getPath('reports-observation')}
        >
          <Icon name="chevron left" size="large" />
          <span className="underline font-bold">
            {i18n.ft(messages.backToReport)}
          </span>
        </Link>
      </div>

      <h1 className="mt-4">{title}</h1>
      <p className="mt-2">{subtitle}</p>

      {accountTemplates?.length === 0 ? (
        <div className="mt-10 py-6 flex gap-1 justify-center align-center bg-ds-white rounded border border-ds-border mb-6">
          <AlertCircle size={20} />
          <p className="mb-4">{i18n.ft(messages.noAssessmentTemplates)}</p>
        </div>
      ) : isLoading ? (
        <PageLoader />
      ) : (
        configurations.map((config, idx) => {
          return (
            <CollapsibleConfigurationForm
              key={idx}
              title={i18n.ft(messages.configNumber, { count: idx + 1 })}
              values={config}
              disableArchivedOptions={idx > 0}
              onRemove={() => handleRemoveComparison(idx)}
              onChange={values => handleConfigChange(idx, values)}
              onTotalChange={total => handleTotalChange(idx, total)}
              disableAgeLevel={idx > 0}
              ageLevelOptions={ageLevelOptions}
              classTemplateOptions={classTemplateOptions}
              currentAccountId={currentAccount?.id ?? 0}
              collapseEnabled={configurations.length > 1}
              isComparisonConfig={configurations.length > 1}
            />
          );
        })
      )}

      <div className="mt-10 mb-20 shadow-xl rounded-[10px]">
        <div className="h-2 bg-[#0A9CCA] rounded-t-[10px]" />
        <div className="py-6 px-7 bg-white rounded-b-[10px] border-x border-b border-[#d2d4d4] flex justify-between">
          <Button inverted color="blue" onClick={() => navigate(-1)}>
            {i18n.ft(messages.back)}
          </Button>

          <div>
            <Button
              inverted
              color="blue"
              disabled={configurations.length > 1}
              onClick={handleAddComparison}
            >
              <Icon name="plus" />
              <span>{i18n.ft(messages.addComparison)}</span>
            </Button>

            <Button
              color="blue"
              disabled={!isSaveActive}
              onClick={handleSubmit}
              className="!ml-6"
            >
              {i18n.ft(messages.generateReport)}
              <Icon name="arrow right" />
            </Button>
          </div>
        </div>
      </div>
    </MainContent>
  );
}

export default ReportConfigurationForm;
